import {
  Badge,
  Button,
  Checkbox,
  DrawerBody,
  DrawerFooter,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { Service } from 'shared/models/service';
import { useCreateResellerCustomerModalContext } from '../ModalContext';

interface Props {
  onClose(): void;
}

const ContractStep: React.FC<Props> = ({ onClose }) => {
  const {
    paymentDay,
    setPaymentDay,
    periodicity,
    setPeriodicity,
    services,
    selectedServiceIds,
    addService,
    removeService,
    contractErrors: errors,
    validateContractStep,
    nextStep,
    previousStep,
  } = useCreateResellerCustomerModalContext();

  const paymentDays = [5, 10, 15, 20];
  const [dayTip, setDayTip] = useState('');

  const updateDayTip = useCallback(() => {
    const today = new Date().getDate();
    const payDate = new Date();
    payDate.setDate(paymentDay);
    if (paymentDay < today) {
      payDate.setMonth(payDate.getMonth() + 1);
    }
    setDayTip(
      `Primeira cobrança dia ${payDate.toLocaleString('pt', {
        day: 'numeric',
        month: 'long',
      })}`,
    );
  }, [paymentDay]);

  useEffect(() => {
    updateDayTip();
  }, [updateDayTip]);

  const handleNext = () => {
    if (validateContractStep()) {
      nextStep();
    }
  };

  const toggleService = (service: Service) => {
    if (!service.id) return;
    if (selectedServiceIds.includes(service.id)) {
      removeService(service.id);
    } else {
      addService(service.id);
    }
  };

  return (
    <>
      <DrawerBody>
        <Stack spacing="1.5rem">
          <HStack spacing="1rem" wrap="wrap">
            <FormControl flexBasis="45%">
              <FormLabel fontSize="0.8rem" color="gray.500">
                Método de pagamento
              </FormLabel>
              <Input value="Cartão de Crédito" isReadOnly bg="gray.50" />
            </FormControl>

            <FormControl flexBasis="45%">
              <FormLabel fontSize="0.8rem" color="gray.500">
                Tipo de contrato
              </FormLabel>
              <Input value="Revenda" isReadOnly bg="gray.50" />
            </FormControl>
          </HStack>

          <HStack spacing="1rem" wrap="wrap">
            <FormControl
              isRequired
              isInvalid={!!errors.due_date}
              flexBasis="45%"
            >
              <FormLabel fontSize="0.8rem" color="gray.500">
                Dia do vencimento
              </FormLabel>
              <Select
                value={paymentDay}
                onChange={(e) => setPaymentDay(Number(e.target.value))}
              >
                {paymentDays.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </Select>
              <FormHelperText>{dayTip}</FormHelperText>
              {errors.due_date && (
                <FormErrorMessage>{errors.due_date}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl flexBasis="45%">
              <FormLabel fontSize="0.8rem" color="gray.500">
                Periodicidade
              </FormLabel>
              <HStack>
                <Button
                  size="sm"
                  variant={periodicity === 'monthly' ? 'solid' : 'outline'}
                  colorScheme="yooga"
                  onClick={() => setPeriodicity('monthly')}
                >
                  Mensal
                </Button>
                <Button
                  size="sm"
                  variant={periodicity === 'yearly' ? 'solid' : 'outline'}
                  colorScheme="yooga"
                  onClick={() => setPeriodicity('yearly')}
                >
                  Anual
                </Button>
              </HStack>
            </FormControl>
          </HStack>

          <FormControl isRequired isInvalid={!!errors.services}>
            <FormLabel fontSize="0.8rem" color="gray.500">
              Serviços
            </FormLabel>
            {services.length === 0 ? (
              <Text color="gray.400" fontSize="sm">
                Nenhum serviço disponível para esta periodicidade
              </Text>
            ) : (
              <Stack
                spacing="0.5rem"
                maxH="300px"
                overflowY="auto"
                border="1px"
                borderColor="gray.200"
                rounded="md"
                p="0.75rem"
              >
                {services.map((service: Service) => (
                  <HStack
                    key={service.id}
                    p="0.5rem"
                    rounded="md"
                    _hover={{ bg: 'gray.50' }}
                    justify="space-between"
                  >
                    <HStack>
                      <Checkbox
                        isChecked={selectedServiceIds.includes(
                          service.id as number,
                        )}
                        onChange={() => toggleService(service)}
                        colorScheme="yooga"
                      />
                      <Text fontSize="sm">{service.name}</Text>
                    </HStack>
                    <Badge colorScheme="green" fontSize="xs">
                      {service.priceString}
                    </Badge>
                  </HStack>
                ))}
              </Stack>
            )}
            {errors.services && (
              <FormErrorMessage>{errors.services}</FormErrorMessage>
            )}
          </FormControl>
        </Stack>
      </DrawerBody>

      <DrawerFooter gap="1rem">
        <Button variant="outline" onClick={previousStep} flex="1">
          Voltar
        </Button>
        <Button colorScheme="yooga" onClick={handleNext} flex="1">
          Próximo
        </Button>
      </DrawerFooter>
    </>
  );
};

export default ContractStep;
